/* eslint-disable react/display-name */
import { IconButton, List, ListItem, Tooltip, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import SelectionIcon from "mdi-react/SelectionIcon";
import SelectionOffIcon from "mdi-react/SelectionOffIcon";
import { Map, MapEvent } from "ol";
import { Extent } from "ol/extent";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  readMapSettings,
  updateMapSettings,
} from "../functions/localMapSettings";
import { zoomOnFeature } from "../functions/zoomOnFeature";
import { LayerTitle } from "../types/layers";
import { MapDataTitle } from "../types/map";
import {
  EventMapItem,
  MapItem,
  PortMapItem,
  VesselMapItem,
} from "../types/mapItems";
import EventListItem from "./EventListItem";
import MapDataList from "./MapDataList";
import PortListItem from "./PortListItem";
import VesselListItem from "./VesselListItem";

const useStyles = makeStyles((theme) => ({
  title: {
    color: theme.palette.primary.contrastText,
  },
  toggleButton: {
    marginLeft: "auto",
    marginRight: theme.spacing(3),
  },
  viewportIcon: {
    fill: theme.palette.primary.contrastText,
  },
  list: {
    height: "100%",
    overflow: "auto",
  },
}));

interface Props {
  map: Map;
}

const MapData = React.forwardRef<HTMLUListElement, Props>(
  ({ map }: Props, ref): JSX.Element => {
    const classes = useStyles();
    const { t } = useTranslation("map");
    const [featuresInCurrentView, setFeaturesInCurrentView] = useState(
      readMapSettings("featuresInCurrentView"),
    );
    const [mapExtent, setMapExtent] = useState<Extent>();

    // Listen to changes in map move
    useEffect(() => {
      const handleMapMove = (evt: MapEvent) => {
        const v = evt.map.getView();
        setMapExtent(v.calculateExtent());
      };

      const view = map.getView();
      map.on("moveend", handleMapMove);
      setMapExtent(view.calculateExtent());
      return () => {
        map.un("moveend", handleMapMove);
      };
    }, [map]);

    const handleToggleFeaturesInCurrentView = () => {
      setFeaturesInCurrentView((prev) => {
        updateMapSettings({ featuresInCurrentView: !prev });
        return !prev;
      });
      const view = map.getView();
      setMapExtent(view.calculateExtent());
    };

    const handleItemClick = (item: MapItem) => {
      zoomOnFeature(map, item.mapId, 18);
      setMapExtent(map.getView().calculateExtent());
    };

    return (
      <List ref={ref} className={classes.list}>
        <ListItem>
          <Typography variant="h4" className={classes.title}>
            {t("map-data-title")}
          </Typography>
          <Tooltip
            title={
              featuresInCurrentView
                ? t("show-all").toString()
                : t("show-in-view").toString()
            }
          >
            <IconButton
              className={classes.toggleButton}
              onClick={handleToggleFeaturesInCurrentView}
              size="large"
            >
              {featuresInCurrentView ? (
                <SelectionOffIcon className={classes.viewportIcon} />
              ) : (
                <SelectionIcon className={classes.viewportIcon} />
              )}
            </IconButton>
          </Tooltip>
        </ListItem>
        <MapDataList<PortMapItem>
          layerTitle={LayerTitle.Ports}
          title={MapDataTitle.Ports}
          defaultOpen
          visibilityToggle
          map={map}
          mapExtent={mapExtent}
          showInViewOnly={featuresInCurrentView}
          render={(port, style) => (
            <PortListItem
              style={style}
              key={port.id}
              port={port}
              onClick={handleItemClick}
            />
          )}
        />
        <MapDataList<VesselMapItem>
          layerTitle={LayerTitle.Vessels}
          title={MapDataTitle.Vessels}
          defaultOpen
          visibilityToggle
          map={map}
          mapExtent={mapExtent}
          showInViewOnly={featuresInCurrentView}
          render={(vessel, style) => (
            <VesselListItem
              style={style}
              key={vessel.mmsi}
              vessel={vessel}
              onClick={handleItemClick}
            />
          )}
        />
        <MapDataList<EventMapItem>
          layerTitle={LayerTitle.Events}
          title={MapDataTitle.Events}
          defaultOpen
          visibilityToggle
          map={map}
          mapExtent={mapExtent}
          showInViewOnly={featuresInCurrentView}
          render={(event, style) => (
            <EventListItem
              style={style}
              key={event.id}
              event={event}
              onClick={handleItemClick}
            />
          )}
        />
      </List>
    );
  },
);
export default MapData;
